import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTabsModule } from '@angular/material/tabs';
import {
  Invoice,
  Payment,
  FinancialSummary,
  MonthlyRevenue,
  AccountingTab,
  InvoiceStatus,
  PaymentStatus
} from '../models/accounting.model';

@Component({
  selector: 'app-accounting',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTabsModule
  ],
  templateUrl: './accounting.component.html',
  styleUrl: './accounting.component.scss'
})
export class AccountingComponent implements OnInit {
  // Tab management
  activeTab: AccountingTab = 'overview';

  // Filter properties
  searchText: string = '';
  selectedStatus: string = '';
  fromDate: Date | null = null;
  toDate: Date | null = null;

  // Data
  invoices: Invoice[] = [];
  payments: Payment[] = [];
  financialSummary: FinancialSummary = {
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    outstandingBalance: 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    overdueInvoices: 0
  };
  monthlyRevenue: MonthlyRevenue[] = [];

  // Status options
  invoiceStatusOptions: string[] = ['All', 'Paid', 'Pending', 'Overdue', 'Partial', 'Cancelled'];
  paymentStatusOptions: string[] = ['All', 'Completed', 'Pending', 'Failed', 'Refunded'];

  // Loading state
  isLoading: boolean = false;

  // Mock data
  private mockInvoices: Invoice[] = [
    {
      invoiceId: 'INV001',
      invoiceNumber: 'INV-2024-0001',
      customerId: 'CUS001',
      customerName: 'Global Logistics Inc',
      orderId: 'ORD-2024-001',
      issueDate: '2024-11-01',
      dueDate: '2024-11-30',
      amount: 5000,
      tax: 500,
      totalAmount: 5500,
      paidAmount: 5500,
      status: 'Paid',
      description: 'Freight services - November 2024'
    },
    {
      invoiceId: 'INV002',
      invoiceNumber: 'INV-2024-0002',
      customerId: 'CUS002',
      customerName: 'Fast Freight Co',
      orderId: 'ORD-2024-002',
      issueDate: '2024-11-05',
      dueDate: '2024-12-05',
      amount: 8500,
      tax: 850,
      totalAmount: 9350,
      paidAmount: 0,
      status: 'Pending',
      description: 'Container shipping - Route A to B'
    },
    {
      invoiceId: 'INV003',
      invoiceNumber: 'INV-2024-0003',
      customerId: 'CUS003',
      customerName: 'Express Shipping Ltd',
      orderId: 'ORD-2024-003',
      issueDate: '2024-10-15',
      dueDate: '2024-11-15',
      amount: 12000,
      tax: 1200,
      totalAmount: 13200,
      paidAmount: 0,
      status: 'Overdue',
      description: 'Bulk cargo transportation'
    },
    {
      invoiceId: 'INV004',
      invoiceNumber: 'INV-2024-0004',
      customerId: 'CUS004',
      customerName: 'Swift Transport',
      orderId: 'ORD-2024-004',
      issueDate: '2024-11-10',
      dueDate: '2024-12-10',
      amount: 3500,
      tax: 350,
      totalAmount: 3850,
      paidAmount: 2000,
      status: 'Partial',
      description: 'Local delivery services'
    },
    {
      invoiceId: 'INV005',
      invoiceNumber: 'INV-2024-0005',
      customerId: 'CUS005',
      customerName: 'Prime Delivery',
      orderId: 'ORD-2024-005',
      issueDate: '2024-11-15',
      dueDate: '2024-12-15',
      amount: 7200,
      tax: 720,
      totalAmount: 7920,
      paidAmount: 7920,
      status: 'Paid',
      description: 'Express freight handling'
    },
    {
      invoiceId: 'INV006',
      invoiceNumber: 'INV-2024-0006',
      customerId: 'CUS001',
      customerName: 'Global Logistics Inc',
      orderId: 'ORD-2024-006',
      issueDate: '2024-11-20',
      dueDate: '2024-12-20',
      amount: 15000,
      tax: 1500,
      totalAmount: 16500,
      paidAmount: 0,
      status: 'Pending',
      description: 'International shipping - December'
    }
  ];

  private mockPayments: Payment[] = [
    {
      paymentId: 'PAY001',
      invoiceId: 'INV001',
      invoiceNumber: 'INV-2024-0001',
      customerName: 'Global Logistics Inc',
      paymentDate: '2024-11-25',
      amount: 5500,
      paymentMethod: 'Bank Transfer',
      referenceNumber: 'TRF-2024-001',
      status: 'Completed',
      notes: 'Full payment received'
    },
    {
      paymentId: 'PAY002',
      invoiceId: 'INV005',
      invoiceNumber: 'INV-2024-0005',
      customerName: 'Prime Delivery',
      paymentDate: '2024-11-28',
      amount: 7920,
      paymentMethod: 'Credit Card',
      referenceNumber: 'CC-2024-002',
      status: 'Completed',
      notes: 'Visa ending 4242'
    },
    {
      paymentId: 'PAY003',
      invoiceId: 'INV004',
      invoiceNumber: 'INV-2024-0004',
      customerName: 'Swift Transport',
      paymentDate: '2024-11-20',
      amount: 2000,
      paymentMethod: 'Cash',
      referenceNumber: 'CASH-2024-003',
      status: 'Completed',
      notes: 'Partial payment - first installment'
    },
    {
      paymentId: 'PAY004',
      invoiceId: 'INV002',
      invoiceNumber: 'INV-2024-0002',
      customerName: 'Fast Freight Co',
      paymentDate: '2024-12-01',
      amount: 9350,
      paymentMethod: 'Bank Transfer',
      referenceNumber: 'TRF-2024-004',
      status: 'Pending',
      notes: 'Awaiting bank confirmation'
    },
    {
      paymentId: 'PAY005',
      invoiceId: 'INV003',
      invoiceNumber: 'INV-2024-0003',
      customerName: 'Express Shipping Ltd',
      paymentDate: '2024-11-18',
      amount: 13200,
      paymentMethod: 'Check',
      referenceNumber: 'CHK-2024-005',
      status: 'Failed',
      notes: 'Check bounced - insufficient funds'
    }
  ];

  private mockMonthlyRevenue: MonthlyRevenue[] = [
    { month: 'Jul', revenue: 45000, expenses: 32000, profit: 13000 },
    { month: 'Aug', revenue: 52000, expenses: 35000, profit: 17000 },
    { month: 'Sep', revenue: 48000, expenses: 33000, profit: 15000 },
    { month: 'Oct', revenue: 61000, expenses: 40000, profit: 21000 },
    { month: 'Nov', revenue: 58000, expenses: 38000, profit: 20000 },
    { month: 'Dec', revenue: 42000, expenses: 28000, profit: 14000 }
  ];

  constructor() {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    setTimeout(() => {
      this.invoices = [...this.mockInvoices];
      this.payments = [...this.mockPayments];
      this.monthlyRevenue = [...this.mockMonthlyRevenue];
      this.calculateFinancialSummary();
      this.isLoading = false;
    }, 500);
  }

  calculateFinancialSummary(): void {
    const totalRevenue = this.monthlyRevenue.reduce((sum, m) => sum + m.revenue, 0);
    const totalExpenses = this.monthlyRevenue.reduce((sum, m) => sum + m.expenses, 0);

    const paidInvoices = this.invoices.filter(i => i.status === 'Paid').length;
    const pendingInvoices = this.invoices.filter(i => i.status === 'Pending' || i.status === 'Partial').length;
    const overdueInvoices = this.invoices.filter(i => i.status === 'Overdue').length;

    const outstandingBalance = this.invoices
      .filter(i => i.status !== 'Paid' && i.status !== 'Cancelled')
      .reduce((sum, i) => sum + (i.totalAmount - i.paidAmount), 0);

    this.financialSummary = {
      totalRevenue,
      totalExpenses,
      netProfit: totalRevenue - totalExpenses,
      outstandingBalance,
      paidInvoices,
      pendingInvoices,
      overdueInvoices
    };
  }

  setActiveTab(tab: AccountingTab): void {
    this.activeTab = tab;
    this.clearFilters();
  }

  get filteredInvoices(): Invoice[] {
    let filtered = [...this.invoices];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.invoiceNumber.toLowerCase().includes(search) ||
        inv.customerName.toLowerCase().includes(search) ||
        inv.orderId.toLowerCase().includes(search)
      );
    }

    if (this.selectedStatus && this.selectedStatus !== 'All') {
      filtered = filtered.filter(inv => inv.status === this.selectedStatus);
    }

    if (this.fromDate) {
      filtered = filtered.filter(inv => new Date(inv.issueDate) >= this.fromDate!);
    }

    if (this.toDate) {
      filtered = filtered.filter(inv => new Date(inv.issueDate) <= this.toDate!);
    }

    return filtered;
  }

  get filteredPayments(): Payment[] {
    let filtered = [...this.payments];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(pay =>
        pay.invoiceNumber.toLowerCase().includes(search) ||
        pay.customerName.toLowerCase().includes(search) ||
        pay.referenceNumber.toLowerCase().includes(search)
      );
    }

    if (this.selectedStatus && this.selectedStatus !== 'All') {
      filtered = filtered.filter(pay => pay.status === this.selectedStatus);
    }

    if (this.fromDate) {
      filtered = filtered.filter(pay => new Date(pay.paymentDate) >= this.fromDate!);
    }

    if (this.toDate) {
      filtered = filtered.filter(pay => new Date(pay.paymentDate) <= this.toDate!);
    }

    return filtered;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = '';
    this.fromDate = null;
    this.toDate = null;
  }

  getInvoiceStatusClass(status: InvoiceStatus): string {
    switch (status) {
      case 'Paid': return 'status-paid';
      case 'Pending': return 'status-pending';
      case 'Overdue': return 'status-overdue';
      case 'Partial': return 'status-partial';
      case 'Cancelled': return 'status-cancelled';
      default: return '';
    }
  }

  getPaymentStatusClass(status: PaymentStatus): string {
    switch (status) {
      case 'Completed': return 'status-completed';
      case 'Pending': return 'status-pending';
      case 'Failed': return 'status-failed';
      case 'Refunded': return 'status-refunded';
      default: return '';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getMaxRevenue(): number {
    return Math.max(...this.monthlyRevenue.map(m => Math.max(m.revenue, m.expenses)));
  }

  getBarHeight(value: number): number {
    const maxValue = this.getMaxRevenue();
    return maxValue > 0 ? (value / maxValue) * 100 : 0;
  }

  viewInvoice(invoice: Invoice): void {
    console.log('View invoice:', invoice);
    // TODO: Implement view invoice detail
  }

  editInvoice(invoice: Invoice): void {
    console.log('Edit invoice:', invoice);
    // TODO: Implement edit invoice
  }

  viewPayment(payment: Payment): void {
    console.log('View payment:', payment);
    // TODO: Implement view payment detail
  }
}
